import { useState } from 'react';
import { toast } from 'sonner';

import {
  exportOrgCaseRecords,
  type ExportOrgCaseRecordsRequest,
  OrgExportSchemaError,
} from '@/services/export.service';
import { useStore } from '@/store/index';
import type { ExportFormat, OrgExportColumnsMode } from '@/types/export.types';

/**
 * Org-wide (Tier-2) case export hook. Mirrors `useDownloadCaseRecords` (toast
 * progress + `isExporting`) but drives the cross-program `exportOrgCaseRecords`
 * pipeline instead of the per-program one — the Tier-2 page has no single selected
 * program, so it fetches via `FindOrganizationCaseRecords`. Columns come from the
 * create-case (aepqc_preprocessing) input schema ('common', default) or from each
 * manufacturer's own full schema, one file per manufacturer ('all').
 *
 * Resolves `true` when at least one file was downloaded (so callers can close
 * their dialog), `false` otherwise.
 */
export const useDownloadOrgCaseRecords = () => {
  const [isExporting, setIsExporting] = useState(false);
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);

  const downloadOrgCaseRecords = async (
    filters: ExportOrgCaseRecordsRequest['filters'],
    workflowNameById: Map<string, string>,
    format: ExportFormat = 'csv',
    columnsMode: OrgExportColumnsMode = 'common',
  ): Promise<boolean> => {
    if (!selectedOrganizationId) {
      toast.error('Organization context is required');
      return false;
    }

    setIsExporting(true);
    const toastId = toast.loading(`Preparing ${format.toUpperCase()} export...`);

    try {
      const result = await exportOrgCaseRecords({
        organizationId: selectedOrganizationId,
        filters,
        workflowNameById,
        columnsMode,
        options: { format, sheetName: 'Case Records' },
        onProgress: progress => {
          toast.loading(progress.message, { id: toastId });
        },
      });

      if (result.failedManufacturers.length > 0) {
        // Partial success: some manufacturers' schemas couldn't be loaded, so
        // their files were skipped. Manufacturer names only — never case data.
        const total = result.files + result.failedManufacturers.length;
        toast.error(
          `Exported ${result.files} of ${total} manufacturer files. ` +
            `Couldn't load columns for: ${result.failedManufacturers.join(', ')}`,
          { id: toastId },
        );
      } else if (columnsMode === 'all') {
        toast.success(
          `Exported ${result.files} ${result.files === 1 ? 'file' : 'files'} (one per manufacturer)`,
          { id: toastId },
        );
      } else {
        toast.success(`Case records exported as ${format.toUpperCase()}`, { id: toastId });
      }
      return true;
    } catch (error) {
      // Never log PHI — only the error object, never the case records.
      console.error('Org export failed:', error);
      // OrgExportSchemaError messages are user-safe (manufacturer names only).
      toast.error(
        error instanceof OrgExportSchemaError ? error.message : 'Failed to export case records',
        { id: toastId },
      );
      return false;
    } finally {
      setIsExporting(false);
    }
  };

  return {
    downloadOrgCaseRecords,
    isExporting,
  };
};
